import type { ClientLocale } from './clientLocale';

type InvoiceStatus = 'Paid' | 'Pending' | string;

export type InvoicePdfCopy = {
  title: string;
  from: string;
  to: string;
  details: string;
  attn: string;
  email: string;
  phone: string;
  taxId: string;
  invoiceNo: string;
  status: string;
  issueDate: string;
  dueDate: string;
  paidDate: string;
  item: string;
  quantity: string;
  unitPrice: string;
  total: string;
  salesTax: string;
  professionalServices: string;
  project: string;
  terms: string;
  memo: string;
  payVia: string;
  payViaMercury: string;
  payViaManual: string;
  payOnline: string;
  payCta: string;
  includeRef: string;
  questions: string;
  mercuryFooter: string;
  statusLabel: (status: InvoiceStatus) => string;
};

export type ReminderCopy = {
  subject: string;
  greeting: string;
  body: string;
  attached: string;
  payCta: string;
  payOnline: string;
  closing: string;
  signoff: string;
};

const invoicePdf: Record<ClientLocale, InvoicePdfCopy> = {
  en: {
    title: 'INVOICE',
    from: 'From',
    to: 'To',
    details: 'Details',
    attn: 'Attn',
    email: 'Email',
    phone: 'Phone',
    taxId: 'Tax ID',
    invoiceNo: 'Invoice no.',
    status: 'Status',
    issueDate: 'Invoice date',
    dueDate: 'Due date',
    paidDate: 'Paid date',
    item: 'Item',
    quantity: 'Quantity',
    unitPrice: 'Unit price',
    total: 'Total',
    salesTax: 'Sales tax',
    professionalServices: 'Professional services',
    project: 'Project',
    terms: 'Terms',
    memo: 'Memo',
    payVia: 'Pay via',
    payViaMercury: 'Manual transfer (ACH/Wire), Pay by Mercury',
    payViaManual: 'Manual transfer',
    payOnline: 'Pay online',
    payCta: 'Pay invoice',
    includeRef: 'Please include invoice ID {{id}} in the payment reference.',
    questions: 'Questions? Contact {{email}}.',
    mercuryFooter:
      'Mercury uses secure proxy account and routing numbers to safely send and protect the sender\'s real details.',
    statusLabel: (status) => {
      if (status === 'Paid') return 'Paid';
      if (status === 'Pending') return 'Pending';
      return status || 'Pending';
    },
  },
  es: {
    title: 'FACTURA',
    from: 'De',
    to: 'Para',
    details: 'Detalle',
    attn: 'Attn',
    email: 'Correo',
    phone: 'Teléfono',
    taxId: 'ID fiscal',
    invoiceNo: 'N.º de factura',
    status: 'Estado',
    issueDate: 'Fecha de factura',
    dueDate: 'Fecha de vencimiento',
    paidDate: 'Fecha de pago',
    item: 'Ítem',
    quantity: 'Cantidad',
    unitPrice: 'Precio unitario',
    total: 'Total',
    salesTax: 'Impuesto',
    professionalServices: 'Servicios profesionales',
    project: 'Proyecto',
    terms: 'Términos',
    memo: 'Nota',
    payVia: 'Pago vía',
    payViaMercury: 'Transferencia manual (ACH/Wire), pago por Mercury',
    payViaManual: 'Transferencia manual',
    payOnline: 'Pagar en línea',
    payCta: 'Pagar factura',
    includeRef: 'Incluya el ID de factura {{id}} en la referencia de pago.',
    questions: '¿Dudas? Escríbanos a {{email}}.',
    mercuryFooter:
      'Mercury usa números de cuenta y ruta proxy para enviar el pago con seguridad y proteger los datos reales del remitente.',
    statusLabel: (status) => {
      if (status === 'Paid') return 'Pagada';
      if (status === 'Pending') return 'Pendiente';
      return status || 'Pendiente';
    },
  },
};

const reminder: Record<ClientLocale, ReminderCopy> = {
  en: {
    subject: 'Payment reminder — Invoice {{id}}',
    greeting: 'Hello {{name}},',
    body: 'This is a friendly reminder that invoice {{id}} for {{amount}} was due on {{date}}.',
    attached: 'The invoice PDF is attached to this email.',
    payCta: 'Pay invoice',
    payOnline: 'Pay online',
    closing: 'Please let us know if you need anything to complete payment.\n\nThank you.',
    signoff: 'Sincerely,',
  },
  es: {
    subject: 'Recordatorio de pago — Factura {{id}}',
    greeting: 'Hola {{name}},',
    body: 'Le recordamos que la factura {{id}} por {{amount}} venció el {{date}}.',
    attached: 'Adjuntamos el PDF de la factura a este correo.',
    payCta: 'Pagar factura',
    payOnline: 'Pagar en línea',
    closing: 'Avísenos si necesita algo para completar el pago.\n\nGracias.',
    signoff: 'Atentamente,',
  },
};

export type QuotePdfCopy = {
  title: string;
  from: string;
  to: string;
  details: string;
  attn: string;
  quoteNo: string;
  status: string;
  issueDate: string;
  validUntil: string;
  item: string;
  description: string;
  quantity: string;
  unitPrice: string;
  tax: string;
  total: string;
  subtotal: string;
  salesTax: string;
  project: string;
  terms: string;
  notes: string;
  termsConditions: string;
  taxId: string;
  footer: string;
  statusLabel: (status: string) => string;
};

const quotePdf: Record<ClientLocale, QuotePdfCopy> = {
  en: {
    title: 'QUOTE',
    from: 'From',
    to: 'To',
    details: 'Details',
    attn: 'Attn',
    quoteNo: 'Quote no.',
    status: 'Status',
    issueDate: 'Issue date',
    validUntil: 'Valid until',
    item: 'Item',
    description: 'Description',
    quantity: 'Qty',
    unitPrice: 'Unit price',
    tax: 'Tax',
    total: 'Total',
    subtotal: 'Subtotal',
    salesTax: 'Sales tax',
    project: 'Project',
    terms: 'Terms',
    notes: 'Notes',
    termsConditions: 'Terms & conditions',
    taxId: 'Tax ID',
    footer: 'Questions about this quote? Contact {{email}}.',
    statusLabel: (status) => status || 'Draft',
  },
  es: {
    title: 'COTIZACIÓN',
    from: 'De',
    to: 'Para',
    details: 'Detalle',
    attn: 'Attn',
    quoteNo: 'N.º de cotización',
    status: 'Estado',
    issueDate: 'Fecha de emisión',
    validUntil: 'Válida hasta',
    item: 'Ítem',
    description: 'Descripción',
    quantity: 'Cant.',
    unitPrice: 'Precio unitario',
    tax: 'Impuesto',
    total: 'Total',
    subtotal: 'Subtotal',
    salesTax: 'Impuesto',
    project: 'Proyecto',
    terms: 'Términos',
    notes: 'Notas',
    termsConditions: 'Términos y condiciones',
    taxId: 'ID fiscal',
    footer: '¿Dudas sobre esta cotización? Escríbanos a {{email}}.',
    statusLabel: (status) => {
      if (status === 'Draft') return 'Borrador';
      if (status === 'Sent') return 'Enviada';
      if (status === 'Accepted') return 'Aceptada';
      if (status === 'Rejected') return 'Rechazada';
      if (status === 'Expired') return 'Expirada';
      return status || 'Borrador';
    },
  },
};

export function invoicePdfCopy(locale: ClientLocale): InvoicePdfCopy {
  return invoicePdf[locale];
}

export function quotePdfCopy(locale: ClientLocale): QuotePdfCopy {
  return quotePdf[locale];
}

export type AgreementPdfCopy = {
  title: string;
  from: string;
  to: string;
  details: string;
  attn: string;
  taxId: string;
  project: string;
  status: string;
  approved: string;
  approvedBy: string;
  approvedAt: string;
  item: string;
  description: string;
  quantity: string;
  unitPrice: string;
  total: string;
  subtotal: string;
  salesTax: string;
  scope: string;
  highlights: string;
  sow: string;
  contract: string;
  resources: string;
  footer: string;
  statusLabel: (status: string) => string;
};

const agreementPdf: Record<ClientLocale, AgreementPdfCopy> = {
  en: {
    title: 'AGREEMENT',
    from: 'From',
    to: 'To',
    details: 'Details',
    attn: 'Attn',
    taxId: 'Tax ID',
    project: 'Project',
    status: 'Status',
    approved: 'Approved',
    approvedBy: 'Approved by',
    approvedAt: 'Approved on',
    item: 'Item',
    description: 'Description',
    quantity: 'Qty',
    unitPrice: 'Unit price',
    total: 'Total',
    subtotal: 'Subtotal',
    salesTax: 'Sales tax',
    scope: 'Scope',
    highlights: 'Highlights',
    sow: 'Statement of work',
    contract: 'Service agreement',
    resources: 'Resources',
    footer: 'Questions about this agreement? Contact {{email}}.',
    statusLabel: (status) => {
      if (status === 'draft') return 'Draft';
      if (status === 'shared') return 'Shared';
      if (status === 'changes_requested') return 'Changes requested';
      if (status === 'approved') return 'Approved';
      return status || 'Draft';
    },
  },
  es: {
    title: 'ACUERDO',
    from: 'De',
    to: 'Para',
    details: 'Detalle',
    attn: 'Attn',
    taxId: 'ID fiscal',
    project: 'Proyecto',
    status: 'Estado',
    approved: 'Aprobado',
    approvedBy: 'Aprobado por',
    approvedAt: 'Aprobado el',
    item: 'Ítem',
    description: 'Descripción',
    quantity: 'Cant.',
    unitPrice: 'Precio unitario',
    total: 'Total',
    subtotal: 'Subtotal',
    salesTax: 'Impuesto',
    scope: 'Alcance',
    highlights: 'Destacados',
    sow: 'Alcance de trabajo',
    contract: 'Acuerdo de servicios',
    resources: 'Recursos',
    footer: '¿Dudas sobre este acuerdo? Escríbanos a {{email}}.',
    statusLabel: (status) => {
      if (status === 'draft') return 'Borrador';
      if (status === 'shared') return 'Compartido';
      if (status === 'changes_requested') return 'Cambios solicitados';
      if (status === 'approved') return 'Aprobado';
      return status || 'Borrador';
    },
  },
};

export function agreementPdfCopy(locale: ClientLocale): AgreementPdfCopy {
  return agreementPdf[locale];
}

export function reminderCopy(locale: ClientLocale): ReminderCopy {
  return reminder[locale];
}

export function fillTemplate(template: string, values: Record<string, string>) {
  return Object.entries(values).reduce(
    (text, [key, value]) => text.replaceAll(`{{${key}}}`, value),
    template
  );
}
